import { color, important, padding, percent, px, rem } from "csx";
import { Content, Horizontal, Stretch } from "gls/lib";
import { classes, style, stylesheet } from "typestyle";
import { Colours } from "../../styles";
import { Icon, IconProps } from "../Icon";
import { Loader } from "../Loader";

export type ButtonProps = {
  icon?: IconProps["icon"];
  size?: "small" | "normal" | "large";
  content?: string;
  fullWidth?: boolean;
  iconPosition?: "left" | "right";
  onClick?: () => void;
  appearance?:
    | "hollow"
    | "transparent"
    | "dark"
    | "blue"
    | "red"
    | "green"
    | "hollow-blue"
    | "hollow-red"
    | "hollow-green";
  disabled?: boolean;
  iconColor?: string;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
  loading?: boolean;
  "data-testid"?: string;
};

export const Button: React.FC<ButtonProps> = ({
  icon,
  iconPosition = "left",
  onClick,
  content,
  appearance = "transparent",
  disabled,
  fullWidth,
  size = "normal",
  iconColor = Colours.textLight,
  type,
  loading,
  "data-testid": dataTestId,
}) => {
  const isDisabled = disabled || loading;
  return (
    <button
      disabled={isDisabled}
      className={classes(
        styles.button,
        content && styles.withContent,
        !isDisabled && styles[appearance],
        fullWidth && "full-width",
        styles[size],
      )}
      onClick={onClick}
      type={type}
      data-testid={dataTestId || "button"}
    >
      <Horizontal
        spacing={rem(0.5)}
        verticalAlign="center"
        horizontalAlign={fullWidth || (icon && !content) ? "center" : "left"}
      >
        {icon && iconPosition === "left" && (
          <Icon icon={icon} color={iconColor} className={styles.icon} />
        )}
        {content && (
          <Content
            className={style({
              visibility: loading ? "hidden" : "visible",
            })}
          >
            {content}
          </Content>
        )}

        {icon && iconPosition === "right" && (
          <Icon icon={icon} color={iconColor} className={styles.icon} />
        )}
      </Horizontal>

      {loading && (
        <Stretch
          className={style({
            position: "absolute",
            top: percent(50),
            left: percent(50),
            transform: "translate(-50%, -50%)",
          })}
          verticalAlign="center"
          horizontalAlign="center"
        >
          <Loader />
        </Stretch>
      )}
    </button>
  );
};

const styles = stylesheet({
  button: {
    minWidth: px(40),
    padding: padding(0, rem(0.5)),
    cursor: "pointer",
    color: Colours.textLight,
    border: "none",
    borderRadius: px(5),
    boxSizing: "border-box",
    outline: "none",
    position: "relative",
    $nest: {
      "&.transparent": {
        backgroundColor: "transparent",
      },
      "&:active": {
        backgroundColor: color(Colours.blue).fadeOut(0.9, true).toHexString(),
      },
      "&:disabled": {
        border: `1px solid ${Colours.darkerBlue}`,
        color: Colours.textDark,
        backgroundColor: "transparent",
        cursor: "default",
      },
      "&.full-width": {
        width: percent(100),
      },
    },
  },
  small: {
    height: rem(2),
  },
  normal: {
    height: rem(2.5),
  },
  large: {
    height: rem(3.5),
  },
  transparent: {
    background: "transparent",
  },
  dark: {
    background: Colours.darkBlue,
    border: `1px solid ${Colours.blue}`,
  },
  blue: {
    background: Colours.primary,
    color: important("white"),
    fontWeight: 600,
    $nest: {
      "&:active": {
        backgroundColor: important(
          color(Colours.primary).lighten(0.1).toHexString(),
        ),
      },
    },
  },
  red: {
    background: Colours.red,
    color: important("white"),
    fontWeight: 600,
    $nest: {
      "&:active": {
        backgroundColor: important(
          color(Colours.red).lighten(0.1).toHexString(),
        ),
      },
    },
  },
  green: {
    background: Colours.green,
    color: important("white"),
    fontWeight: 600,
    $nest: {
      "&:active": {
        backgroundColor: important(
          color(Colours.green).lighten(0.1).toHexString(),
        ),
      },
    },
  },
  hollow: {
    border: `1px solid ${Colours.blue}`,
    backgroundColor: "transparent",
  },
  "hollow-blue": {
    border: `1px solid ${Colours.primary}`,
    backgroundColor: "transparent",
  },
  "hollow-red": {
    border: `1px solid ${Colours.red}`,
    backgroundColor: "transparent",
  },
  "hollow-green": {
    border: `1px solid ${Colours.green}`,
    backgroundColor: "transparent",
  },
  icon: {
    height: rem(1),
    width: rem(1),
  },
  withContent: {
    padding: important(padding(0, rem(1))),
  },
});
